// frontend/src/utils/widgetUtils.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Castle Traffic Dev Team
// Created:      2025-07-14
// Updated:      2025-07-22
//
// Description:  Utility functions for handling widget naming, sorting, filtering, and search label formatting.
//               ✅ Added input validation to prevent React from breaking when passed non-string widget IDs.

/**
 * Returns a friendly title for a widget key.
 * Falls back to humanized snake_case → Title Case.
 *
 * @param {string} key - The widget ID (e.g., "job_list")
 * @param {Array} catalog - The widget catalog containing {name, friendly_name}
 * @returns {string} - The best available title
 */
export function getWidgetTitle(key, catalog = []) {
  if (typeof key !== "string") {
    console.error("[widgetUtils] Invalid widget key type:", key);
    return "Unknown Widget";
  }

  const found = Array.isArray(catalog)
    ? catalog.find((w) => w && typeof w.name === "string" && w.name === key)
    : null;

  return (
    found?.friendly_name ||
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/**
 * Returns a user-facing "Search the ..." label based on widget title.
 *
 * @param {string} key - The widget ID
 * @param {Array} catalog - The widget catalog (optional)
 * @returns {string} - e.g., "Search the Job List"
 */
export function getSearchLabelTitle(key, catalog = []) {
  const title = getWidgetTitle(key, catalog);
  return `Search the ${title}`;
}

/**
 * Filters and sorts a list of items.
 *
 * @param {Array<Object>} data - Array of objects to search and sort.
 * @param {string} query - Search input from user.
 * @param {string[]} fields - Keys in the object to search in.
 * @param {string} sortField - Field name to sort the result by.
 * @param {boolean} ascending - If true, sorts A→Z; if false, sorts Z→A.
 * @returns {Array<Object>} - Filtered and sorted data.
 */
export function filterAndSortList(data, query, fields, sortField, ascending = true) {
  if (!Array.isArray(data)) return [];

  const lowerQuery = String(query || "").toLowerCase();

  const filtered = lowerQuery
    ? data.filter((item) =>
        fields.some((field) =>
          String(item?.[field] ?? "")
            .toLowerCase()
            .includes(lowerQuery)
        )
      )
    : data;

  const sorted = [...filtered].sort((a, b) => {
    const aVal = String(a?.[sortField] ?? "").toLowerCase();
    const bVal = String(b?.[sortField] ?? "").toLowerCase();

    return ascending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

  return sorted;
}

